import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type {
  RealEstateProperty,
  RealEstateProjectPhase,
  RealEstateProjectTask,
  ProjectPhaseStatus,
} from '../types';
import { asRealEstatePropertyType } from '../lib/realEstate';
import { db } from '../lib/firebase';
import { COLLECTION_REAL_ESTATE_ITEMS } from '../lib/firestorePaths';

export type RealEstateWorkspaceState = {
  realEstateItems: RealEstateProperty[];
  loading: boolean;
  error: string | null;
};

const EMPTY_STATE: RealEstateWorkspaceState = {
  realEstateItems: [],
  loading: false,
  error: null,
};

function docItemId(d: QueryDocumentSnapshot, data: { id?: unknown }): string {
  if (typeof data.id === 'string' && data.id.trim()) return data.id.trim();
  const parts = d.id.split('__');
  return parts.length > 1 ? parts.slice(1).join('__') : d.id;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

const VALID_PHASE_STATUSES: ProjectPhaseStatus[] = ['pending', 'in_progress', 'completed', 'blocked'];

function parseProjectPhase(raw: unknown): RealEstateProjectPhase | null {
  if (!raw || typeof raw !== 'object') return null;
  const x = raw as Record<string, unknown>;
  const id = optionalString(x.id);
  const name = optionalString(x.name);
  if (!id || !name) return null;
  const status = (VALID_PHASE_STATUSES.includes(x.status as ProjectPhaseStatus)
    ? x.status
    : 'pending') as ProjectPhaseStatus;
  return {
    id,
    name,
    status,
    dependsOn: Array.isArray(x.dependsOn) ? x.dependsOn.filter((d) => typeof d === 'string') : undefined,
    expectedStartDate: optionalString(x.expectedStartDate),
    expectedEndDate: optionalString(x.expectedEndDate),
    completedDate: optionalString(x.completedDate),
    responsibleParty: optionalString(x.responsibleParty),
    notes: optionalString(x.notes),
    sortOrder: typeof x.sortOrder === 'number' ? x.sortOrder : 0,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : new Date().toISOString(),
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : new Date().toISOString(),
  };
}

function parseProjectTask(raw: unknown): RealEstateProjectTask | null {
  if (!raw || typeof raw !== 'object') return null;
  const x = raw as Record<string, unknown>;
  const id = optionalString(x.id);
  const phaseId = optionalString(x.phaseId);
  const title = optionalString(x.title);
  if (!id || !phaseId || !title) return null;
  return {
    id,
    phaseId,
    title,
    done: x.done === true,
    dueDate: optionalString(x.dueDate),
    assignee: optionalString(x.assignee),
    notes: optionalString(x.notes),
    addToGlobalTasks: x.addToGlobalTasks === true,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : new Date().toISOString(),
    completedAt: typeof x.completedAt === 'string' ? x.completedAt : null,
  };
}

function docToRealEstateProperty(d: QueryDocumentSnapshot): RealEstateProperty {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const currentValue =
    typeof x.currentValue === 'number' && Number.isFinite(x.currentValue) ? x.currentValue : 0;

  const projectPhases = Array.isArray(x.projectPhases)
    ? x.projectPhases.map(parseProjectPhase).filter((p): p is RealEstateProjectPhase => p !== null)
    : undefined;
  const projectTasks = Array.isArray(x.projectTasks)
    ? x.projectTasks.map(parseProjectTask).filter((t): t is RealEstateProjectTask => t !== null)
    : undefined;

  return {
    id: docItemId(d, x),
    userId: typeof x.userId === 'string' ? x.userId : '',
    name: typeof x.name === 'string' && x.name.trim() ? x.name.trim() : 'Property',
    propertyType: asRealEstatePropertyType(x.propertyType),
    address: typeof x.address === 'string' && x.address.trim() ? x.address.trim() : undefined,
    websiteUrl:
      typeof x.websiteUrl === 'string' && x.websiteUrl.trim() ? x.websiteUrl.trim() : undefined,
    currentValue,
    purchasePrice: optionalNumber(x.purchasePrice),
    purchaseDate:
      typeof x.purchaseDate === 'string' && x.purchaseDate.trim() ? x.purchaseDate.trim() : undefined,
    mortgageBalance: optionalNumber(x.mortgageBalance),
    monthlyRent: optionalNumber(x.monthlyRent),
    appreciationRate: optionalNumber(x.appreciationRate),
    notes: typeof x.notes === 'string' && x.notes.trim() ? x.notes.trim() : undefined,
    projectPhases: projectPhases && projectPhases.length > 0 ? projectPhases : undefined,
    projectTasks: projectTasks && projectTasks.length > 0 ? projectTasks : undefined,
    valueUpdatedAt: typeof x.valueUpdatedAt === 'string' ? x.valueUpdatedAt : undefined,
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

export function useRealEstateWorkspaceState(userId: string): RealEstateWorkspaceState {
  const [realEstateItems, setRealEstateItems] = useState<RealEstateProperty[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setRealEstateItems([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    const qItems = query(
      collection(db, COLLECTION_REAL_ESTATE_ITEMS),
      where('userId', '==', userId)
    );

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const itemsSnap = await getDocs(qItems);
        if (cancelled) return;

        setRealEstateItems(itemsSnap.docs.map(docToRealEstateProperty));

        unsubscribers.push(
          onSnapshot(
            qItems,
            (snap) => setRealEstateItems(snap.docs.map(docToRealEstateProperty)),
            (e) => setError(e.message)
          )
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load real estate data');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [userId]);

  return useMemo(
    () => ({ realEstateItems, loading, error }),
    [realEstateItems, loading, error]
  );
}

export function emptyRealEstateWorkspaceState(): RealEstateWorkspaceState {
  return EMPTY_STATE;
}
